import type { RefObject } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { QRLogo } from '@/types/qrCode';

interface QRLogoControlsSectionProps {
  logoEnabled: boolean;
  logoUrl: string | undefined;
  logoSize: number;
  logoMargin: number;
  logos: QRLogo[];
  isUploadingLogo?: boolean;
  logoInputRef: RefObject<HTMLInputElement | null>;
  onToggleLogoEnabled: () => void;
  onSelectDefaultLogo: () => void;
  onSelectLogoUrl: (url: string) => void;
  onDeleteLogo?: (id: string) => void;
  onLogoFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLogoSizeChange: (v: number) => void;
  onLogoMarginChange: (v: number) => void;
}

export function QRLogoControlsSection({
  logoEnabled,
  logoUrl,
  logoSize,
  logoMargin,
  logos,
  isUploadingLogo,
  logoInputRef,
  onToggleLogoEnabled,
  onSelectDefaultLogo,
  onSelectLogoUrl,
  onDeleteLogo,
  onLogoFileChange,
  onLogoSizeChange,
  onLogoMarginChange,
}: QRLogoControlsSectionProps) {
  return (
    <div className="w-full space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">
          Center Logo
        </span>
        <button
          type="button"
          onClick={onToggleLogoEnabled}
          className={cn(
            'text-xs font-medium px-2.5 py-1 rounded transition-colors',
            logoEnabled ? 'text-brand bg-brand/10' : 'text-[var(--text-secondary)] bg-white/5'
          )}
        >
          {logoEnabled ? 'On' : 'Off'}
        </button>
      </div>

      {logoEnabled && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={onSelectDefaultLogo}
              className={cn(
                'w-12 h-12 rounded-lg border-2 bg-white/5 flex items-center justify-center overflow-hidden shrink-0 transition-all',
                !logoUrl ? 'border-brand' : 'border-white/10 hover:border-white/30'
              )}
              title="HT Default"
            >
              <img src="/ht-logo.png" alt="Default" className="w-full h-full object-contain p-1" />
            </button>

            {logos.map((logo) => (
              <div key={logo.id} className="relative group">
                <button
                  type="button"
                  onClick={() => onSelectLogoUrl(logo.file_url)}
                  className={cn(
                    'w-12 h-12 rounded-lg border-2 bg-white/5 flex items-center justify-center overflow-hidden shrink-0 transition-all',
                    logoUrl === logo.file_url ? 'border-brand' : 'border-white/10 hover:border-white/30'
                  )}
                  title={logo.name}
                >
                  <img src={logo.file_url} alt={logo.name} className="w-full h-full object-contain p-1" />
                </button>
                {onDeleteLogo && (
                  <button
                    type="button"
                    onClick={() => onDeleteLogo(logo.id)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete logo"
                  >
                    <Trash2 size={10} />
                  </button>
                )}
              </div>
            ))}

            <label
              className={cn(
                'w-12 h-12 rounded-lg border-2 border-dashed border-white/20 hover:border-white/40 flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-main)] transition-colors shrink-0 cursor-pointer',
                isUploadingLogo && 'pointer-events-none opacity-50'
              )}
              title="Upload logo"
            >
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="sr-only"
                onChange={onLogoFileChange}
              />
              {isUploadingLogo ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <ImagePlus size={16} />
              )}
            </label>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-[10px] text-[var(--text-secondary)] w-8">Size</span>
            <input
              type="range"
              min={10}
              max={50}
              value={Math.round(logoSize * 100)}
              onChange={(e) => onLogoSizeChange(Number(e.target.value) / 100)}
              className="flex-1 h-1 accent-brand"
            />
            <span className="text-[10px] text-[var(--text-secondary)] tabular-nums w-8">
              {Math.round(logoSize * 100)}%
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-[10px] text-[var(--text-secondary)] w-8">Gap</span>
            <input
              type="range"
              min={0}
              max={20}
              value={logoMargin}
              onChange={(e) => onLogoMarginChange(Number(e.target.value))}
              className="flex-1 h-1 accent-brand"
            />
            <span className="text-[10px] text-[var(--text-secondary)] tabular-nums w-8">{logoMargin}px</span>
          </div>
        </div>
      )}
    </div>
  );
}
